/**
 * History for one KPI at the signed-in user's location.
 * Grain groups latest actuals; strategic targets overlay when their period covers a bucket.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import api from '@/services/api';
import { KpiHistoryChart, type HistoryChartPoint } from '@/components/business-performance/performance/KpiHistoryChart';
import { fmtKpiWithUnit } from '@/components/business-performance/performance/types';

type Grain = 'month' | 'quarter' | 'year';

interface HistoryPayload {
  needsHierarchy?: boolean;
  message?: string;
  kpiId?: string;
  kpiName?: string;
  unit?: string | null;
  type?: string | null;
  grain?: Grain;
  scope?: {
    businessName: string | null;
    functionalName: string | null;
    geographicName: string | null;
  };
  points?: HistoryChartPoint[];
  hasStrategic?: boolean;
}

const GRAINS: Grain[] = ['month', 'quarter', 'year'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readGrain(raw: string | null): Grain {
  if (raw === 'quarter' || raw === 'year') return raw;
  return 'month';
}

export const KpiHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const grain = readGrain(searchParams.get('grain'));
  const [data, setData] = useState<HistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!id || !UUID_RE.test(id)) {
      setData(null);
      setError(t('kpi.historyNotFound') || 'KPI not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/business-performance/kpis/${id}/history`, {
        params: { grain }
      });
      setData((response.data?.data || null) as HistoryPayload | null);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setData(null);
      setError(
        status === 404 || status === 400
          ? t('kpi.historyNotFound') || 'KPI not found.'
          : t('kpi.historyLoadFailed') || 'Could not load KPI history.'
      );
    } finally {
      setLoading(false);
    }
  }, [id, grain, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setGrain = (next: Grain) => {
    setSearchParams({ grain: next }, { replace: true });
  };

  const kpi = { type: data?.type || null, unit: data?.unit || null };
  const points = data?.points || [];
  const tableRows = points.filter((point) => point.actual !== null || point.strategic !== null);
  const scopeLabel = [data?.scope?.businessName, data?.scope?.functionalName, data?.scope?.geographicName]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={() => navigate('/performance')} className="flex items-center gap-1">
          <ArrowLeftIcon className="h-4 w-4" />
          <span>{t('kpi.historyBack') || 'Performance dashboard'}</span>
        </Button>
        {id && UUID_RE.test(id) && (
          <Link to={`/kpis/${id}`} className="text-xs text-[var(--color-primary-text)] hover:underline">
            {t('kpi.kpiDetails') || 'KPI details'}
          </Link>
        )}
      </div>

      <div>
        <h1 className="text-xl font-semibold text-[var(--color-surface-text)]">
          {data?.kpiName || t('kpi.historyTitle') || 'KPI history'}
        </h1>
        <p className="text-xs text-[var(--color-secondary-text)]">
          {scopeLabel || t('kpi.perfPersonalScope') || 'Your hierarchy'}
        </p>
        <p className="text-xs text-[var(--color-secondary-text)] mt-0.5">
          {t('kpi.historyHint') ||
            'Actuals and strategic targets at your location. Grain groups uploaded values — it does not roll children up.'}
        </p>
      </div>

      <div className="inline-flex rounded border border-[var(--color-border)] overflow-hidden" role="group">
        {GRAINS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setGrain(option)}
            className={`px-3 py-1 text-xs ${
              grain === option
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
            }`}
          >
            {t(`kpi.historyGrain${option.charAt(0).toUpperCase()}${option.slice(1)}`) || option}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-sm text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && data?.needsHierarchy && (
        <p className="text-sm text-[var(--color-secondary-text)]">
          {data.message || t('kpi.historyNeedsHierarchy')}
        </p>
      )}

      {!loading && data && !data.needsHierarchy && (
        <>
          {tableRows.length === 0 ? (
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('kpi.historyEmpty') || 'No actuals or strategic targets at this location yet.'}
            </p>
          ) : (
            <Card>
              <CardContent className="p-3">
                <KpiHistoryChart points={points} kpi={kpi} t={t} />
                {!data.hasStrategic && (
                  <p className="text-[11px] text-[var(--color-secondary-text)] mt-1">
                    {t('kpi.historyNoStrategic') || 'No strategic target covers this timeline at this location.'}
                  </p>
                )}
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[var(--color-secondary-text)]">
                        <th className="py-1 pr-2 font-medium">{t('kpi.historyPeriod') || 'Period'}</th>
                        <th className="py-1 pr-2 font-medium">{t('kpi.historyActual') || 'Actual'}</th>
                        <th className="py-1 font-medium">{t('kpi.historyStrategic') || 'Strategic target'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row) => (
                        <tr key={row.key} className="border-t border-[var(--color-border)]">
                          <td className="py-1 pr-2 text-[var(--color-surface-text)]">{row.label}</td>
                          <td className="py-1 pr-2 font-medium text-[var(--color-surface-text)]">
                            {fmtKpiWithUnit(kpi, row.actual)}
                          </td>
                          <td className="py-1 text-[var(--color-surface-text)]">
                            {fmtKpiWithUnit(kpi, row.strategic)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
